import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import {
  TextField,
  CircularProgress,
  Grid,
  IconButton,
  Button,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import AttachmentIcon from "@mui/icons-material/Attachment";
import PropTypes from "prop-types";

import { CONSUMER, READ_ADMIN_ROLES } from "common/constant";
import Tooltip from "Components/Common/Tooltip";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";

import getFileExtension from "utils/getFileExtension";

import { usePostUploadExtraDocumentsMutation } from "apis/DocumentType";

import { StyledCommentInput } from "./style";

const docFiles = ["docx", "doc"];

const validFileFormats = ["jpg", "png", "jpeg", "pdf", ...docFiles];

const CommentInput = ({
  handleSubmit,
  applicationId,
  t,
  isFireSafety,
  inspectionObservationId,
}) => {
  const ref = useRef();
  const [comment, setComment] = useState("");
  const [documentToPreview, setDocumentToPreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedDocument, setUploadedDocument] = useState("");

  const { roles } = useSelector((state) => state.Login);
  const isReadAdmin = roles?.some((role) => READ_ADMIN_ROLES.includes(role));

  const [uploadExtraDocument, { isLoading: isExtraDocumentUploading }] =
    usePostUploadExtraDocumentsMutation();

  const commentSubmitHandler = async () => {
    setIsLoading(true);
    try {
      await handleSubmit(comment.trim(), inspectionObservationId);
      setComment("");
    } catch (error) {}
    setIsLoading(false);
  };

  const handleUploadFile = async (selectedFile) => {
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("applicationId", applicationId);

      await uploadExtraDocument(formData);
      setDocumentToPreview(null);
    } catch (error) {}
  };

  const fileChangeHandler = (event) => {
    try {
      const file = event.target.files[0];
      const fileExtension = getFileExtension(file.name).toLowerCase();

      if (file.size > 20971520) {
        toast.error(
          "Please upload image or pdf file and size should be less than 20 mb"
        );
      } else if (!validFileFormats.includes(fileExtension)) {
        toast.error(
          "Invalid file format, Please upload image, pdf or word file"
        );
      } else if (docFiles.includes(fileExtension)) {
        handleUploadFile(event.target.files[0]);
      } else {
        setDocumentToPreview({
          url: URL.createObjectURL(event.target.files[0]),
          documentTypeName: event.target.files[0].name,
          documentType: fileExtension,
          documentName: event.target.files[0].name,
        });

        setUploadedDocument(event.target.files[0]);
        event.target.value = "";
      }
      ref.current.value = "";
    } catch (err) {}
  };

  return (
    <StyledCommentInput container>
      {!!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => setDocumentToPreview(null)}
          isDownloadable={false}
          actionButton={
            <Button
              onClick={() => handleUploadFile(uploadedDocument)}
              color="primary"
              className="mb-1 me-1"
              variant="contained"
              disabled={isLoading}
            >
              {t("Upload")}
              {isExtraDocumentUploading && <CircularProgress size={20} />}
            </Button>
          }
          {...documentToPreview}
        />
      )}
      <Grid item xs={12}>
        <TextField
          placeholder={t("Add a comment") + "..."}
          className="mb-1"
          multiline
          rows={3}
          disabled={isLoading || isReadAdmin}
          value={comment}
          onChange={(e) => {
            setComment(e.target.value);
          }}
          InputProps={{
            endAdornment: (
              <div
                style={{
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                }}
              >
                {roles.includes(CONSUMER) && !isFireSafety && (
                  <Tooltip title={t("Upload Document")}>
                    <IconButton
                      color="primary"
                      component="label"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <CircularProgress size={20} />
                      ) : (
                        <>
                          <AttachmentIcon />
                          <input
                            ref={ref}
                            style={{
                              display: "none",
                            }}
                            accept="image/*, .pdf, .doc, .docx, .PDF, .DOC, .DOCX"
                            type="file"
                            onChange={fileChangeHandler}
                          />
                        </>
                      )}
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title={t("Send")}>
                  <IconButton
                    color="primary"
                    onClick={commentSubmitHandler}
                    disabled={isLoading || !comment.trim().length}
                  >
                    {isLoading ? <CircularProgress size={20} /> : <SendIcon />}
                  </IconButton>
                </Tooltip>
              </div>
            ),
          }}
          fullWidth
          size="small"
        />
      </Grid>
    </StyledCommentInput>
  );
};

CommentInput.propTypes = {
  handleSubmit: PropTypes.func,
  applicationId: PropTypes.number,
  isFireSafety: PropTypes.bool,
  inspectionObservationId: PropTypes.number,
  t: PropTypes.func,
};

export default withTranslation()(CommentInput);
